/**
 * Contact Visits — Iteration 121.2 (Phase A · Full workflow)
 *
 * Two-action entry: "Schedule visit" (planned in advance, editable until
 * it starts) and "Record completed visit" (visit that already happened).
 *
 * Lifecycle: scheduled → confirmed → in_progress → completed → (verified
 * | disputed | cancelled | missed) → closed. Factual fields lock at
 * in_progress. Post-completion edits create a new version snapshot
 * rather than overwriting the original record.
 *
 * Notes are categorised:
 *   • Shared factual      — visible to both parents
 *   • Private             — owner-only, AES-GCM encrypted
 *   • Third-party obs.    — parents + invited facilitator (iter B portal)
 *   • Dispute response    — visible to both parents
 *
 * Every visit has an evidence reference (CV-YYYY-NNNNNN) that the
 * "Copy evidence reference" button packages into a citation suitable
 * for solicitor correspondence and court documents.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { InlineError, StatusPill } from "@/components/primitives";
import { InfoTooltip, HelpLink } from "@/components/InfoTooltip";
import {
  ShieldCheck, CalendarDays, FileText, AlertTriangle, CheckCircle2, Plus,
  MapPin, Clock, UserRoundCheck, X, ChevronRight, ChevronDown, Info, Lock, Users,
  Trash2, Copy, Play, Square, History, Pencil, Eye, BadgeCheck,
  MessageSquare, ScrollText, Zap, Repeat,
} from "lucide-react";
import { toast } from "sonner";

// ===== shared labels & enums (kept inline to minimise file churn) ============

const QUICK_TYPES = [
  { v: "parent_to_parent_handover", l: "Handover",      icon: UserRoundCheck },
  { v: "supervised_contact",        l: "Supervised",    icon: ShieldCheck },
  { v: "school_collection",         l: "School pickup", icon: CalendarDays },
  { v: "school_dropoff",            l: "School drop-off", icon: CalendarDays },
  { v: "overnight_contact",         l: "Overnight",     icon: Clock },
  { v: "parenting_time",            l: "Parenting time", icon: FileText },
];
const MORE_TYPES = [
  ["contact_centre",           "Contact centre"],
  ["third_party_handover",     "Third-party handover"],
  ["holiday_contact",          "Holiday contact"],
  ["telephone_contact",        "Telephone"],
  ["video_contact",            "Video call"],
  ["special_occasion",         "Special occasion"],
  ["other",                    "Other"],
];

const QUICK_OUTCOMES = [
  { v: "completed",        l: "Completed",     tone: "bg-emerald-700 hover:bg-emerald-800 text-white" },
  { v: "completed_late",   l: "Late",          tone: "bg-amber-600 hover:bg-amber-700 text-white" },
  { v: "cancelled",        l: "Cancelled",     tone: "bg-slate-600 hover:bg-slate-700 text-white" },
  { v: "refused",          l: "Refused",       tone: "bg-rose-700 hover:bg-rose-800 text-white" },
  { v: "parent_unable",    l: "Parent unable", tone: "bg-slate-500 hover:bg-slate-600 text-white" },
  { v: "child_unable",     l: "Child unable",  tone: "bg-slate-500 hover:bg-slate-600 text-white" },
];

const ROLE_OPTIONS = [
  ["contact_supervisor", "Contact supervisor"],
  ["grandparent",        "Grandparent"],
  ["social_worker",      "Social worker"],
  ["foster_carer",       "Foster carer"],
  ["mediator",           "Mediator"],
  ["relative",           "Relative"],
  ["friend",             "Friend"],
  ["guardian_ad_litem",  "Guardian ad litem"],
  ["interpreter",        "Interpreter"],
  ["professional",       "Professional"],
  ["other",              "Other"],
];

const NOTE_TABS = [
  { v: "shared_factual",         l: "Shared factual",   hint: "Visible to both parents in this household." },
  { v: "private",                l: "Private",          hint: "Visible only to you. Encrypted at rest." },
  { v: "third_party_observation",l: "Third-party",      hint: "Visible to parents and invited facilitators." },
  { v: "dispute_response",       l: "Dispute response", hint: "Visible to both parents. Available for disputed visits only." },
];

const STATUS_TONE = {
  scheduled:               { chip: "bg-slate-100",   text: "text-slate-700" },
  confirmed:               { chip: "bg-sky-100",     text: "text-sky-900" },
  in_progress:             { chip: "bg-amber-100",   text: "text-amber-900" },
  awaiting_verification:   { chip: "bg-amber-100",   text: "text-amber-900" },
  verified:                { chip: "bg-emerald-100", text: "text-emerald-800" },
  verified_by_professional:{ chip: "bg-emerald-100", text: "text-emerald-800" },
  completed:               { chip: "bg-emerald-50",  text: "text-emerald-700" },
  cancelled:               { chip: "bg-slate-100",   text: "text-slate-500" },
  rescheduled:             { chip: "bg-amber-100",   text: "text-amber-900" },
  disputed:                { chip: "bg-rose-100",    text: "text-rose-900" },
  missed:                  { chip: "bg-rose-50",     text: "text-rose-700" },
  closed:                  { chip: "bg-slate-100",   text: "text-slate-500" },
};

function statusLabel(s) { return (s || "scheduled").replace(/_/g, " "); }
function typeLabel(t)   { return (t || "parenting_time").replace(/_/g, " "); }

function StatusBadge({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.scheduled;
  return (
    <Badge className={`${t.chip} ${t.text} border-0 rounded-sm text-[10px] uppercase tracking-wider`}>
      {statusLabel(status)}
    </Badge>
  );
}

function todayISO() { return new Date().toISOString().slice(0, 10); }
function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// ===== Tile ===================================================================

// (Local `Tile` helper removed in iter 122.m4 — superseded by the
// design-system `KpiTile` primitive imported above.)

// ===== People Present editor =================================================

function PeoplePresentEditor({ value, onChange }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("contact_supervisor");

  const add = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onChange([...(value || []), { name: trimmed, role }]);
    setName("");
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {value?.length > 0 && (
        <ul className="space-y-1.5" data-testid="cv-people-list">
          {value.map((p, i) => {
            const roleLabel = (ROLE_OPTIONS.find((r) => r[0] === p.role) || ["other","Other"])[1];
            return (
              <li key={i} className="flex items-center gap-2 bg-slate-50 rounded-sm px-2.5 py-2" data-testid={`cv-person-${i}`}>
                <span className="text-sm text-slate-900 flex-1 truncate">{p.name}</span>
                <Badge className="bg-teal-50 text-teal-800 border border-teal-200 rounded-sm text-[10px] uppercase tracking-wider font-medium">
                  {roleLabel}
                </Badge>
                <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-rose-700 p-1" aria-label="Remove" data-testid={`cv-person-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="grid grid-cols-[1fr,auto,auto] gap-2 items-center">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Name"
          className="h-11 text-base"
          data-testid="cv-person-name-input"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="h-11 text-sm border border-slate-200 rounded-sm px-2 bg-white"
          data-testid="cv-person-role-select"
        >
          {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Button type="button" onClick={add} variant="outline" className="h-11 rounded-sm px-3" data-testid="cv-person-add">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ===== Copy Evidence Reference ==============================================

function formatCitation(visit) {
  const date = visit.visit_date
    ? new Date(visit.visit_date).toLocaleDateString("en-GB", {
        day: "numeric", month: "long", year: "numeric",
      })
    : "";
  const type = typeLabel(visit.visit_type)
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const status = statusLabel(visit.status)
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${visit.evidence_ref} | ${type} | ${date} | Status: ${status}`;
}

function InlineCopyIcon({ text, testid, tooltip = "Copy" }) {
  // Iter 122.cv-copy-tweak — small icon-only button. The evidence
  // reference is now selectable text and this just provides a one-tap
  // convenience for users who want the full citation on the
  // clipboard. The big primary "Copy reference" button used to live
  // here; it was demoted because copying an internal ref is not a
  // visit-management action — see user UX note.
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Reference copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy. Long-press the reference instead.");
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={tooltip}
      aria-label={tooltip}
      className="inline-flex items-center justify-center w-6 h-6 rounded-sm text-[#6F828A] hover:text-[#116B6E] hover:bg-[#EEF3F4] transition-colors"
      data-testid={testid}
    >
      {copied
        ? <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2} />
        : <Copy className="w-3.5 h-3.5" strokeWidth={1.7} />}
    </button>
  );
}

// ===== Schedule / Record form (shared) ======================================

const EMPTY_FORM = {
  visit_type: "parent_to_parent_handover",
  visit_date: todayISO(),
  start_time: nowHHMM(),
  end_time: "",
  location: "",
  child_ids: [],
  people_present: [],
  notes: "",
  outcome: null,
  pending_invites: [],
};

function VisitFormSheet({ open, mode, onClose, onSaved, onSwitchMode, householdChildren, initial }) {
  // mode: "schedule" (no outcome) | "record" (outcome required) | "edit" (patch)
  const [form, setForm] = useState(EMPTY_FORM);
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      // Edit mode pre-fill
      const start = initial.scheduled_start || "";
      const end = initial.scheduled_end || "";
      setForm({
        visit_type: initial.visit_type || "parent_to_parent_handover",
        visit_date: initial.visit_date || todayISO(),
        start_time: start.slice(11, 16) || nowHHMM(),
        end_time: end.slice(11, 16) || "",
        location: initial.location || "",
        child_ids: initial.child_ids || [],
        people_present: initial.people_present || [],
        notes: "",                 // not editable here — use notes tab
        outcome: null,
        pending_invites: [],
      });
    } else if (mode === "schedule") {
      // Default to a date one day ahead at 10:00 for planning UX.
      const d = new Date();
      d.setDate(d.getDate() + 1);
      setForm({ ...EMPTY_FORM,
        visit_date: d.toISOString().slice(0, 10),
        start_time: "10:00",
      });
    } else {
      setForm({ ...EMPTY_FORM, visit_date: todayISO(), start_time: nowHHMM() });
    }
  }, [open, mode, initial]);

  const toggleChild = (id) => setForm((f) => {
    const has = f.child_ids.includes(id);
    return { ...f, child_ids: has ? f.child_ids.filter((c) => c !== id) : [...f.child_ids, id] };
  });
  const selectAllChildren = () => setForm((f) => ({ ...f, child_ids: [] }));

  const submit = async () => {
    if (!form.visit_date || !form.start_time) {
      toast.error("Pick a date and start time"); return;
    }
    setSaving(true);
    try {
      const startIso = `${form.visit_date}T${form.start_time}:00+00:00`;
      const endIso   = form.end_time ? `${form.visit_date}T${form.end_time}:00+00:00` : startIso;
      const payload = {
        visit_type: form.visit_type,
        visit_date: form.visit_date,
        scheduled_start: startIso,
        scheduled_end: endIso,
        location: form.location || undefined,
        child_ids: form.child_ids,
        people_present: form.people_present,
        notes: form.notes || undefined,
      };
      if (mode === "edit" && initial) {
        // PATCH (factual fields only — notes go via /notes endpoint).
        const patchBody = {
          visit_type: payload.visit_type,
          visit_date: payload.visit_date,
          scheduled_start: payload.scheduled_start,
          scheduled_end: payload.scheduled_end,
          location: payload.location,
          child_ids: payload.child_ids,
          people_present: payload.people_present,
        };
        await api.patch(`/contact-visits/${initial.id}`, patchBody);
        toast.success("Scheduled visit updated");
      } else {
        const created = await api.post("/contact-visits", payload);
        if (mode === "record" && form.outcome) {
          // Recording a completed visit: stamp the outcome straight away
          // so it transitions to awaiting_verification.
          try {
            await api.post(`/contact-visits/${created.data.id}/outcome`, {
              outcome: form.outcome,
              parent_arrival: startIso,
            });
          } catch (e) { console.warn("outcome submit", e?.response?.data || e); }
        }
        // Iter 122.observers-at-scheduling — send any pending observer
        // invites as part of the same submission. Failures are toasted
        // individually but never block the happy path.
        if (form.pending_invites && form.pending_invites.length > 0) {
          const results = await sendPendingObserverInvites(created.data.id, form.pending_invites);
          const okCount = results.filter((r) => r.ok).length;
          const lastLink = results.find((r) => r.ok && r.link)?.link;
          try { if (lastLink) await navigator.clipboard?.writeText(lastLink); } catch { /* clipboard blocked */ }
          if (okCount > 0) {
            toast.success(
              okCount === 1 && lastLink
                ? "Observer invited · secure link copied"
                : `${okCount} observer${okCount === 1 ? "" : "s"} invited`,
            );
          }
        }
        toast.success(`Visit ${mode === "schedule" ? "scheduled" : "recorded"} · ${created.data.evidence_ref || ""}`);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const allChildrenSelected = (form.child_ids?.length || 0) === 0;
  const titleText = mode === "edit"
    ? "Edit scheduled visit"
    : "New contact visit";
  const subTitle = mode === "edit"
    ? "Factual edits are recorded on the audit trail. Once the visit begins these fields are locked."
    : mode === "schedule"
    ? "Schedule a contact visit. Add the key details now and update the record as the visit progresses. Every change is securely logged to maintain a complete audit trail."
    : "Document a visit that has already taken place. Build a secure, chronological record with shared factual notes, optional private notes and supporting records.";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="bottom" className="rounded-t-xl p-0 max-h-[92vh] overflow-y-auto sm:max-w-2xl sm:mx-auto" data-testid="cv-quick-recorder">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <SheetTitle className="text-base font-semibold flex items-center gap-2">
            {mode === "edit" ? <Pencil className="w-4 h-4 text-teal-700" /> : <Plus className="w-4 h-4 text-teal-700" />}
            {titleText}
          </SheetTitle>
          <p className="text-xs text-slate-500 text-left leading-relaxed">{subTitle}</p>
          {mode !== "edit" && (
            <div className="mt-2 grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-sm" data-testid="cv-form-mode-toggle">
              <button type="button" onClick={() => onSwitchMode?.("schedule")}
                className={`text-xs font-medium h-9 rounded-sm transition-colors ${
                  mode === "schedule" ? "bg-white text-teal-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="cv-mode-schedule">
                Scheduled visit
              </button>
              <button type="button" onClick={() => onSwitchMode?.("record")}
                className={`text-xs font-medium h-9 rounded-sm transition-colors ${
                  mode === "record" ? "bg-white text-teal-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="cv-mode-record">
                Already taken place
              </button>
            </div>
          )}
        </SheetHeader>

        <div className="px-5 py-4 space-y-5">
          {/* Visit type */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Visit type</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {QUICK_TYPES.map(({ v, l, icon: Icon }) => (
                <button key={v} type="button"
                  onClick={() => setForm((f) => ({ ...f, visit_type: v }))}
                  data-testid={`cv-quick-type-${v}`}
                  className={`min-h-[68px] flex flex-col items-center justify-center gap-1 rounded-sm border text-xs px-2 py-2 transition-colors ${
                    form.visit_type === v ? "border-teal-700 bg-teal-50 text-teal-900"
                                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}>
                  <Icon className="w-4 h-4" strokeWidth={1.6} />
                  <span className="leading-tight text-center">{l}</span>
                </button>
              ))}
            </div>
            {!showMore ? (
              <button type="button" onClick={() => setShowMore(true)}
                className="mt-2 text-xs text-teal-700 hover:underline" data-testid="cv-quick-type-more">
                Show all visit types
              </button>
            ) : (
              <select value={form.visit_type}
                onChange={(e) => setForm({ ...form, visit_type: e.target.value })}
                className="mt-2 w-full text-sm border border-slate-200 rounded-sm h-10 px-2 bg-white" data-testid="cv-quick-type-select">
                {[...QUICK_TYPES.map((t) => [t.v, t.l]), ...MORE_TYPES].map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            )}
          </section>

          {/* Children */}
          {householdChildren?.length > 0 && (
            <section>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Children
              </Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button type="button" onClick={selectAllChildren} data-testid="cv-child-all"
                  className={`min-h-[40px] px-3 rounded-full text-xs font-medium transition-colors ${
                    allChildrenSelected ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}>
                  All children
                </button>
                {householdChildren.map((c) => {
                  const selected = form.child_ids.includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => toggleChild(c.id)}
                      data-testid={`cv-child-${c.id}`}
                      className={`min-h-[40px] px-3 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                        selected ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}>
                      {c.colour && <span className="w-2 h-2 rounded-full" style={{ background: c.colour }} />}
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* When */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">When</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] text-slate-500">Date</Label>
                <Input type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="h-11 text-base" data-testid="cv-quick-date" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Start</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="h-11 text-base" data-testid="cv-quick-start" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">End <span className="text-slate-400">(opt)</span></Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="h-11 text-base" data-testid="cv-quick-end" />
              </div>
            </div>
          </section>

          {/* Outcome — only for "record" mode */}
          {mode === "record" && (
            <section>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Visit outcome</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {QUICK_OUTCOMES.map((o) => {
                  const active = form.outcome === o.v;
                  return (
                    <button key={o.v} type="button"
                      onClick={() => setForm((f) => ({ ...f, outcome: active ? null : o.v }))}
                      data-testid={`cv-quick-outcome-${o.v}`}
                      className={`min-h-[44px] rounded-sm text-xs font-medium px-2 transition-all ${
                        active ? `${o.tone} shadow-sm ring-2 ring-offset-1 ring-teal-300`
                               : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                      }`}>
                      {o.l}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[11px] text-slate-500 flex items-start gap-1.5">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                The outcome can be reviewed and verified by both parents after submission.
              </p>
            </section>
          )}

          {/* Location */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Location (optional)</Label>
            <div className="mt-2 relative">
              <MapPin className="absolute left-2.5 top-3.5 w-4 h-4 text-slate-400" />
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Contact centre, school, park" className="h-11 text-base pl-9" data-testid="cv-quick-location" />
            </div>
          </section>

          {/* People present */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <UserRoundCheck className="w-3 h-3" /> Participants (optional)
            </Label>
            <p className="text-[11px] text-slate-500 mt-1 mb-2 leading-relaxed">
              Add anyone present during the visit &mdash; supervisors, grandparents, social workers, foster carers, mediators.
            </p>
            <PeoplePresentEditor value={form.people_present} onChange={(arr) => setForm({ ...form, people_present: arr })} />
          </section>

          {/* Observer invitations — only schedule/record modes */}
          {mode !== "edit" && (
            <section>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <UserRoundCheck className="w-3 h-3" /> Invite observers (optional)
              </Label>
              <p className="text-[11px] text-slate-500 mt-1 mb-2 leading-relaxed">
                Each observer receives a secure single-use link by email when the visit is saved.
                The link is also copied to your clipboard so you can share it another way.
              </p>
              <PendingObserverEditor
                value={form.pending_invites}
                onChange={(arr) => setForm({ ...form, pending_invites: arr })}
                testidPrefix="cv-form-obs"
              />
            </section>
          )}

          {/* Private note — not shown in edit mode (use notes tab on detail) */}
          {mode !== "edit" && (
            <section>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Lock className="w-3 h-3" /> Private notes (author-only)
              </Label>
              <p className="text-[11px] text-slate-500 mt-1 mb-2 leading-relaxed">
                Encrypted at rest. Visible to you only &mdash; not to other participants. You can add shared factual notes from the visit&apos;s detail screen later.
              </p>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Brief, factual observations…" className="text-base" data-testid="cv-quick-notes" />
            </section>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-12 rounded-sm" data-testid="cv-quick-cancel">Cancel</Button>
          <Button type="button" onClick={submit} disabled={saving}
            className="flex-[2] h-12 rounded-sm bg-teal-700 hover:bg-teal-800 text-white text-base font-medium" data-testid="cv-quick-submit">
            {saving ? "Saving…"
             : mode === "edit"     ? "Save changes"
             : mode === "schedule" ? "Schedule visit"
             :                       "Document visit"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ===== Detail panel =========================================================

function NotesTab({ visit, onAdded }) {
  const [activeTab, setActiveTab] = useState("shared_factual");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const tabNotes = useMemo(() => {
    const all = visit.notes || [];
    return all.filter((n) => (n.category || "private") === activeTab);
  }, [visit, activeTab]);

  const isDisputeTab = activeTab === "dispute_response";
  const showDisputeTab = visit.status === "disputed" || tabNotes.length > 0;

  const submit = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await api.post(`/contact-visits/${visit.id}/notes`, { body: draft, category: activeTab });
      setDraft("");
      toast.success("Note added");
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="cv-notes-tab">
      <div className="flex flex-wrap gap-1.5 border-b border-slate-100 pb-2">
        {NOTE_TABS.map((t) => {
          if (t.v === "dispute_response" && !showDisputeTab) return null;
          const active = activeTab === t.v;
          return (
            <button key={t.v} type="button" onClick={() => setActiveTab(t.v)}
              data-testid={`cv-notes-tab-${t.v}`}
              className={`text-xs px-2.5 py-1.5 rounded-sm transition-colors ${
                active ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}>{t.l}</button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500">
        {(NOTE_TABS.find((t) => t.v === activeTab) || {}).hint}
      </p>
      {tabNotes.length === 0 ? (
        <p className="text-xs text-slate-500">No notes in this category yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {tabNotes.map((n) => (
            <li key={n.id} className={`text-sm rounded-sm p-2.5 ${
              activeTab === "private" ? "bg-slate-50" : "bg-teal-50/40 border border-teal-100/60"
            }`} data-testid={`cv-note-${n.id}`}>
              {n.body}
              <div className="text-[10px] text-slate-500 mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
            </li>
          ))}
        </ul>
      )}
      {(!isDisputeTab || visit.status === "disputed") && (
        <div className="space-y-2">
          <Textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder={`Add a ${(NOTE_TABS.find((t) => t.v === activeTab) || {}).l?.toLowerCase()} note…`}
            data-testid="cv-notes-add-input" />
          <Button onClick={submit} disabled={saving || !draft.trim()} variant="outline" className="rounded-sm" data-testid="cv-notes-add-submit">
            {saving ? "Adding…" : "Add note"}
          </Button>
        </div>
      )}
    </div>
  );
}

function VersionsTab({ visit }) {
  const [versions, setVersions] = useState(null);
  useEffect(() => {
    if (!visit?.id) return;
    api.get(`/contact-visits/${visit.id}/versions`)
      .then((r) => setVersions(r.data || []))
      .catch(() => setVersions([]));
  }, [visit?.id]);
  if (versions === null) return <p className="text-xs text-slate-500">Loading version history…</p>;
  if (versions.length === 0) {
    return (
      <p className="text-xs text-slate-500" data-testid="cv-versions-empty">
        No earlier versions. The current record is the original — every post-completion edit will appear here.
      </p>
    );
  }
  return (
    <ul className="space-y-2" data-testid="cv-versions-list">
      {versions.map((v) => (
        <li key={v.id} className="bg-slate-50 rounded-sm p-2.5 text-xs">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-medium text-slate-900">v{v.seq}</span>
            <span className="text-slate-500 font-mono-data tabular-nums">{v.snapshotted_at?.slice(0, 16).replace("T", " ")}</span>
          </div>
          <div className="text-slate-600 mt-1">{v.reason || "Pre-edit snapshot"}</div>
        </li>
      ))}
    </ul>
  );
}

function ActiveSessionPanel({ visit, onChanged }) {
  // Iter 122.cv-overhaul — Live visit session UI.
  //
  // Per user spec: "I don't actually think users care about watching a
  // timer count upwards." So we DON'T render a ticking timer. Instead
  // we show start time + a static elapsed label calculated from
  // `actual_start`. Duration is computed authoritatively from
  // timestamps on End.
  //
  // The start time is editable (PATCH /start-time) until End — real
  // life happens. Every edit is audit-logged on the backend.
  const [editing, setEditing] = useState(false);
  const [startTime, setStartTime] = useState(
    (visit.actual_start || "").slice(11, 16),
  );
  const [saving, setSaving] = useState(false);

  const elapsedLabel = useMemo(() => {
    if (!visit.actual_start) return "—";
    try {
      const started = new Date(visit.actual_start);
      const ms = Date.now() - started.getTime();
      if (ms < 0) return "Just now";
      const mins = Math.floor(ms / 60000);
      if (mins < 1) return "Just now";
      if (mins < 60) return `${mins} min`;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m ? `${h}h ${m}m` : `${h}h`;
    } catch { return "—"; }
  }, [visit.actual_start]);

  const saveStartTime = async () => {
    if (!startTime) { toast.error("Pick a time"); return; }
    const datePart = (visit.actual_start || new Date().toISOString()).slice(0, 10);
    const iso = `${datePart}T${startTime}:00+00:00`;
    setSaving(true);
    try {
      await api.patch(`/contact-visits/${visit.id}/start-time`, { actual_start: iso });
      toast.success("Start time updated");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update start time");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#A7D8DA] bg-[#D6F0EF]/40 rounded-md p-3.5 space-y-3" data-testid="cv-active-session-panel">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1F9FA4] opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1F9FA4]" />
        </span>
        <span className="text-[11px] uppercase tracking-[0.1em] text-[#0F5658] font-medium">
          Visit in progress
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6F828A]">Started</div>
          {editing ? (
            <div className="flex items-center gap-1.5 mt-0.5">
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="h-8 text-sm" data-testid="cv-active-start-input" />
              <Button onClick={saveStartTime} disabled={saving}
                className="h-8 px-2 rounded-sm bg-[#1F9FA4] hover:bg-[#178085] text-white text-xs"
                data-testid="cv-active-start-save">
                {saving ? "…" : "Save"}
              </Button>
              <Button onClick={() => { setEditing(false); setStartTime((visit.actual_start || "").slice(11, 16)); }}
                variant="outline" className="h-8 px-2 rounded-sm text-xs"
                data-testid="cv-active-start-cancel">
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[#0F5658] font-medium font-mono-data tabular-nums">
                {(visit.actual_start || "").slice(11, 16) || "—"}
              </span>
              <button type="button" onClick={() => setEditing(true)}
                className="text-[11px] text-[#116B6E] hover:underline"
                data-testid="cv-active-edit-start">
                Edit
              </button>
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6F828A]">Elapsed</div>
          <div className="text-[#0F5658] font-medium font-mono-data tabular-nums mt-0.5"
            data-testid="cv-active-elapsed">
            {elapsedLabel}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-[#6F828A] leading-snug flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        This session is saved to your account and will be here when you come back, even if you close the app.
      </p>
    </div>
  );
}

function ObserversTab({ visit }) {
  // Iter 122.m4b — Parent-side facilitator UI.
  //
  // Two stacked lists:
  //   1. Invites — pending / submitted / expired / revoked. Pending
  //      invites get a "Copy link" + "Revoke" action. Submitted ones
  //      get a "Download PDF" action via the linked submission.
  //   2. Immutable observations — every submission attached to this
  //      visit. The PDF download here is the evidence-grade per-
  //      submission PDF (audit_id, hash, IP, UA, observer, signature).
  //
  // Iter 122.m4c — also exposes:
  //   - "Download combined PDF" of every observation on the visit
  //     (cover sheet + each submission), only when ≥1 observation.
  //   - "Forward to solicitor" per observation: opens a dialog,
  //     sends via Resend with the PDF attached, records the forward
  //     on the observation document. History is shown inline.
  const [invites, setInvites] = useState([]);
  const [observations, setObservations] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: "", name: "", organisation: "",
    role: "contact_supervisor", context_note: "",
  });
  // Iter 122.m4c — dialog state for "Forward to solicitor".
  const [forwardTarget, setForwardTarget] = useState(null);
  const [bundleBusy, setBundleBusy] = useState(false);

  const reload = async () => {
    try {
      const [inv, obs] = await Promise.all([
        api.get(`/contact-visits/${visit.id}/facilitator-invites`),
        api.get(`/contact-visits/${visit.id}/third-party-observations`),
      ]);
      setInvites(inv.data?.invites || []);
      setObservations(obs.data?.observations || []);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || e.message);
    }
  };
  useEffect(() => { reload(); }, [visit.id]);

  const sendInvite = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.name) {
      toast.error("Email and name are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(
        `/contact-visits/${visit.id}/facilitator-invites`, form,
      );
      const link = r.data?.one_time_link;
      try { if (link) await navigator.clipboard?.writeText(link); } catch { /* clipboard may be blocked */ }
      toast.success(link ? "Invite sent — link copied to clipboard" : "Invite sent");
      setShowInvite(false);
      setForm({ email: "", name: "", organisation: "", role: "contact_supervisor", context_note: "" });
      await reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || (typeof d === "string" ? d : "Could not create invite"));
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (inviteId) => {
    if (!window.confirm("Revoke this invite? The observer will no longer be able to submit.")) return;
    try {
      await api.post(`/contact-visits/${visit.id}/facilitator-invites/${inviteId}/revoke`);
      toast.success("Invite revoked");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    }
  };

  const downloadPdf = async (submissionId) => {
    try {
      const res = await api.get(
        `/contact-visits/${visit.id}/third-party-observations/${submissionId}/pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `observation-${submissionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const downloadBundlePdf = async () => {
    setBundleBusy(true);
    try {
      const res = await api.get(
        `/contact-visits/${visit.id}/third-party-observations.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `observations-bundle-${(visit.evidence_ref || visit.id || "").toString().replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate combined PDF");
    } finally {
      setBundleBusy(false);
    }
  };

  const sendForward = async (submissionId, payload) => {
    try {
      const r = await api.post(
        `/contact-visits/${visit.id}/third-party-observations/${submissionId}/forward`,
        payload,
      );
      // Resend may not be configured in this environment — we still
      // log the forward in the audit trail. Surface either outcome
      // honestly.
      if (r.data?.ok) {
        toast.success(`Sent to ${payload.recipient_email}`);
      } else {
        toast.warning(
          `Logged the forward, but email delivery failed (${r.data?.error || "unknown error"}). The audit trail still records the attempt.`,
        );
      }
      await reload();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not forward");
      return false;
    }
  };

  return (
    <div className="space-y-5" data-testid="cv-observers-tab">
      {loadErr && <InlineError testId="cv-observers-error">{String(loadErr)}</InlineError>}

      <header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-xs uppercase tracking-[0.12em] text-[#6F828A] font-medium">
            Third-party observers
          </h3>
          <p className="text-[11px] text-[#6F828A] leading-snug mt-0.5 max-w-md">
            Invite a contact-centre supervisor, mediator, social worker or other
            professional to submit an independent observation. Links expire after
            30 days and submissions are locked permanently.
          </p>
        </div>
        <Button onClick={() => setShowInvite((s) => !s)}
          className={"rounded-sm text-xs h-8 px-3 " + (showInvite
            ? "bg-white border border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4]"
            : "bg-[#1F9FA4] hover:bg-[#178085] text-white")}
          data-testid="cv-observers-invite-toggle">
          {showInvite ? "Cancel" : "+ Invite observer"}
        </Button>
      </header>

      {showInvite && (
        <form onSubmit={sendInvite} className="border border-[#D9E1E3] rounded-md bg-[#F4FAFA] p-4 space-y-3" data-testid="cv-observers-invite-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-[#2E3A3F]">Email *</Label>
              <Input type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cv-observers-invite-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-[#2E3A3F]">Full name *</Label>
              <Input required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="cv-observers-invite-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-[#2E3A3F]">Role</Label>
              <select value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full h-10 px-3 rounded-sm border border-[#D9E1E3] bg-white text-sm"
                data-testid="cv-observers-invite-role">
                <option value="contact_supervisor">Contact-centre supervisor</option>
                <option value="mediator">Mediator</option>
                <option value="therapist">Therapist / counsellor</option>
                <option value="teacher">Teacher</option>
                <option value="social_worker">Social worker</option>
                <option value="family_member">Family member</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-[#2E3A3F]">Organisation</Label>
              <Input value={form.organisation}
                onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                data-testid="cv-observers-invite-org" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-[#2E3A3F]">Note to the observer (optional)</Label>
            <Textarea rows={2} value={form.context_note} maxLength={600}
              onChange={(e) => setForm({ ...form, context_note: e.target.value })}
              placeholder="E.g. Please record arrival and departure as you observed them."
              data-testid="cv-observers-invite-note" />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button type="submit" disabled={submitting}
              className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm text-xs h-9 px-4"
              data-testid="cv-observers-invite-submit">
              {submitting ? "Sending…" : "Send secure link"}
            </Button>
          </div>
          <p className="text-[11px] text-[#6F828A] leading-snug flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            The link is emailed to them and copied to your clipboard for backup. They will not see your account.
          </p>
        </form>
      )}

      <div data-testid="cv-observers-invite-list">
        <h4 className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] font-medium mb-2">Invites</h4>
        {invites.length === 0 ? (
          <p className="text-xs text-[#6F828A] py-3" data-testid="cv-observers-invites-empty">
            No observers have been invited for this visit yet.
          </p>
        ) : (
          <ul className="divide-y divide-[#D9E1E3] border border-[#D9E1E3] rounded-md overflow-hidden bg-white">
            {invites.map((inv) => (
              <li key={inv.id} className="px-3 py-2.5" data-testid={`cv-invite-row-${inv.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-[#2E3A3F]">{inv.name}</span>
                  <span className="text-xs text-[#6F828A]">{inv.email}</span>
                  <InviteStatusPill
                    status={inv.status}
                    expiresAt={inv.expires_at}
                    openedAt={inv.opened_at}
                    startedAt={inv.started_at}
                  />
                </div>
                <div className="mt-0.5 text-[11px] text-[#6F828A] font-mono-data tabular-nums">
                  {(inv.organisation || "—")} · {ROLE_LABEL(inv.role)} · invited {(inv.created_at || "").slice(0, 10)} · expires {(inv.expires_at || "").slice(0, 10)}
                </div>
                {inv.status === "pending" && (
                  <div className="mt-2 flex items-center gap-2">
                    <Button onClick={() => revoke(inv.id)} variant="outline"
                      className="rounded-sm text-[11px] h-7 px-2 text-rose-700 border-rose-200 hover:bg-rose-50"
                      data-testid={`cv-invite-revoke-${inv.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Revoke
                    </Button>
                  </div>
                )}
                {inv.status === "submitted" && inv.submission_id && (
                  <div className="mt-2">
                    <Button onClick={() => downloadPdf(inv.submission_id)} variant="outline"
                      className="rounded-sm text-[11px] h-7 px-2"
                      data-testid={`cv-invite-pdf-${inv.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> Download submission PDF
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div data-testid="cv-observations-list">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h4 className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] font-medium">
            Immutable observations
          </h4>
          {observations.length > 0 && (
            <Button onClick={downloadBundlePdf} disabled={bundleBusy} variant="outline"
              className="rounded-sm text-[11px] h-7 px-2"
              data-testid="cv-observations-bundle-pdf">
              <FileText className="w-3 h-3 mr-1" />
              {bundleBusy ? "Building…" : "Download all as one PDF"}
            </Button>
          )}
        </div>
        {observations.length === 0 ? (
          <p className="text-xs text-[#6F828A] py-3" data-testid="cv-observations-empty">
            No observations submitted yet. Once an observer submits, the locked record will appear here.
          </p>
        ) : (
          <ul className="space-y-3">
            {observations.map((o) => (
              <li key={o.id} className="border border-[#D9E1E3] rounded-md p-3.5 bg-white" data-testid={`cv-observation-${o.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#2E3A3F]">
                      {o.observer?.name}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[#6F828A] font-medium">{ROLE_LABEL(o.observer?.role)}</span>
                    </div>
                    <div className="text-[11px] text-[#6F828A] font-mono-data tabular-nums">
                      submitted {(o.submitted_at || "").slice(0, 16).replace("T", " ")} · audit {o.audit_id?.slice(0, 8)}…
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill tone="positive" testId={`cv-observation-${o.id}-locked-pill`}>
                      <Lock className="w-2.5 h-2.5 mr-1 inline-block" /> Locked
                    </StatusPill>
                    <Button onClick={() => downloadPdf(o.id)} variant="outline"
                      className="rounded-sm text-[11px] h-7 px-2"
                      data-testid={`cv-observation-pdf-${o.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> PDF
                    </Button>
                    <Button onClick={() => setForwardTarget(o)} variant="outline"
                      className="rounded-sm text-[11px] h-7 px-2 text-[#116B6E] border-[#A7D8DA] hover:bg-[#D6F0EF]"
                      data-testid={`cv-observation-forward-${o.id}`}>
                      <MessageSquare className="w-3 h-3 mr-1" /> Forward
                    </Button>
                  </div>
                </div>
                {Array.isArray(o.forwards) && o.forwards.length > 0 && (
                  <ForwardHistory forwards={o.forwards} obsId={o.id} />
                )}
                <ObservationDetails o={o} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {forwardTarget && (
        <ForwardDialog
          target={forwardTarget}
          onClose={() => setForwardTarget(null)}
          onSend={async (payload) => {
            const ok = await sendForward(forwardTarget.id, payload);
            if (ok) setForwardTarget(null);
          }}
        />
      )}
    </div>
  );
}

function ForwardHistory({ forwards, obsId }) {
  return (
    <div className="mt-3 pt-3 border-t border-[#D9E1E3]" data-testid={`cv-observation-${obsId}-forwards`}>
      <div className="text-[10px] uppercase tracking-wider text-[#6F828A] font-medium mb-1.5">
        Forwarded to
      </div>
      <ul className="space-y-1">
        {forwards.map((f) => (
          <li key={f.id || f.audit_id} className="flex items-start gap-2 text-[11px]"
            data-testid={`cv-forward-${f.audit_id || f.id}`}>
            <span className={"inline-flex h-1.5 w-1.5 rounded-full mt-1.5 flex-shrink-0 " + (f.email_ok ? "bg-[#1F9FA4]" : "bg-amber-500")} />
            <span className="flex-1 min-w-0">
              <span className="text-[#2E3A3F]">{f.recipient_name || f.recipient_email}</span>
              {f.recipient_name && <span className="text-[#6F828A]"> &lt;{f.recipient_email}&gt;</span>}
              <span className="text-[#6F828A] font-mono-data tabular-nums"> · {(f.forwarded_at || "").slice(0, 16).replace("T", " ")}</span>
              {f.forwarded_by_name && <span className="text-[#6F828A]"> · by {f.forwarded_by_name}</span>}
              {!f.email_ok && (
                <span className="block text-amber-700 text-[10px] mt-0.5">
                  Email delivery failed: {f.email_error || "unknown error"} — forward recorded in audit trail.
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ForwardDialog({ target, onClose, onSend }) {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!recipientEmail.trim()) return;
    setSending(true);
    try {
      await onSend({
        recipient_email: recipientEmail.trim(),
        recipient_name: recipientName.trim() || null,
        sender_message: message.trim() || null,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="cv-forward-dialog">
      <div className="bg-white border border-[#D9E1E3] rounded-t-md sm:rounded-md shadow-xl w-full sm:max-w-md p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h3 className="font-heading text-lg font-medium text-[#2E3A3F]">Forward observation</h3>
            <p className="text-[11px] text-[#6F828A] mt-0.5">
              Sends the locked PDF to the recipient, with a copy in the audit trail.
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="text-[#6F828A] hover:text-[#2E3A3F] p-1 -m-1"
            aria-label="Close"
            data-testid="cv-forward-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs font-medium text-[#2E3A3F]">Recipient email *</Label>
            <Input type="email" required autoFocus
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="e.g. solicitor@firm.co.uk"
              data-testid="cv-forward-email" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-[#2E3A3F]">Recipient name (optional)</Label>
            <Input value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="e.g. Jane Smith"
              maxLength={120}
              data-testid="cv-forward-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-[#2E3A3F]">Message (optional)</Label>
            <Textarea rows={3} value={message} maxLength={2000}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a short note for the recipient."
              data-testid="cv-forward-message" />
          </div>
          <p className="text-[11px] text-[#6F828A] leading-snug flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            We don&apos;t save recipient addresses. You&apos;ll be asked again next time.
          </p>
          <div className="pt-1 flex items-center justify-end gap-2">
            <Button type="button" onClick={onClose} variant="outline"
              className="rounded-sm text-xs h-9 px-3"
              data-testid="cv-forward-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={sending}
              className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm text-xs h-9 px-4"
              data-testid="cv-forward-send">
              {sending ? "Sending…" : "Send & log forward"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ObservationDetails({ o }) {
  const [open, setOpen] = useState(false);
  const obs = o.observation || {};
  return (
    <div className="mt-3 pt-3 border-t border-[#D9E1E3]">
      <button type="button" onClick={() => setOpen((s) => !s)}
        className="text-xs text-[#116B6E] hover:text-[#0F5658] font-medium inline-flex items-center gap-1"
        data-testid={`cv-observation-${o.id}-toggle`}>
        <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (open ? "rotate-180" : "")} />
        {open ? "Hide details" : "Show full observation"}
      </button>
      {open && (
        <div className="mt-2.5 space-y-2.5 text-sm text-[#2E3A3F]">
          {obs.safeguarding_flag && (
            <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-sm text-xs text-rose-900">
              <span className="font-medium uppercase tracking-wider text-[10px]">Safeguarding concern raised</span>
              <p className="mt-0.5 leading-snug text-rose-900 whitespace-pre-wrap">{obs.safeguarding_details}</p>
            </div>
          )}
          {[
            ["Arrival", obs.arrival_observation],
            ["Departure", obs.departure_observation],
            ["Child presentation", obs.child_presentation],
            ["Concerns observed", obs.concerns_observed],
            ["Additional observations", obs.additional_observations],
          ].filter(([, v]) => v).map(([l, v]) => (
            <div key={l}>
              <div className="text-[10px] uppercase tracking-wider text-[#6F828A] font-medium">{l}</div>
              <p className="text-sm text-[#2E3A3F] mt-0.5 whitespace-pre-wrap leading-snug">{v}</p>
            </div>
          ))}
          <div className="pt-2 mt-2 border-t border-[#D9E1E3] text-[11px] text-[#6F828A] font-mono-data tabular-nums">
            <div>signed: {o.declaration?.typed_name}</div>
            <div>signed_at: {o.declaration?.signed_at}</div>
            <div>hash: {o.submission_hash?.slice(0, 24)}…</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Location overview (venue + GPS history) =============================
// The visit overview surfaces BOTH the typed venue (visit.location) and
// any captured GPS readings (visit.gps_history). Each reading shows the
// phase (start / end), source (auto-GPS vs user-entered), accuracy and
// timestamp so the parent can see at a glance what the secure record
// holds. We never overwrite a reading — the array is append-only.
function LocationOverview({ visit }) {
  const history = Array.isArray(visit?.gps_history) ? visit.gps_history : [];
  const hasVenue = !!(visit?.location && visit.location.trim());
  if (!hasVenue && history.length === 0) {
    return <span className="text-slate-500">— not recorded</span>;
  }
  const formatCoord = (p) => {
    const lat = typeof p?.lat === "number" ? p.lat.toFixed(5) : "—";
    const lng = typeof p?.lng === "number" ? p.lng.toFixed(5) : "—";
    return `${lat}, ${lng}`;
  };
  const PHASE_LABEL  = { start: "Start", end: "End" };
  const SOURCE_LABEL = { auto_gps: "auto GPS", user_entered: "manual" };
  return (
    <div className="space-y-2" data-testid="cv-detail-location">
      {hasVenue && (
        <div className="flex items-start gap-1.5">
          <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-500 flex-shrink-0" />
          <span className="text-slate-900">{visit.location}</span>
        </div>
      )}
      {history.length > 0 && (
        <ul className="space-y-1.5" data-testid="cv-detail-gps-history">
          {history.map((p, i) => {
            const mapsUrl = (typeof p?.lat === "number" && typeof p?.lng === "number")
              ? `https://www.google.com/maps?q=${p.lat},${p.lng}`
              : null;
            return (
              <li key={i}
                className="flex items-start gap-2 px-2.5 py-2 rounded-sm bg-[#F4FAFA] border border-[#D9E1E3]"
                data-testid={`cv-detail-gps-${i}`}>
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-[#1F9FA4] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-900 flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium">
                      {PHASE_LABEL[p?.phase] || "Reading"} location
                    </span>
                    <Badge className="bg-white text-slate-700 border border-slate-200 rounded-sm text-[10px] uppercase tracking-wider font-medium">
                      {SOURCE_LABEL[p?.source] || (p?.source || "captured")}
                    </Badge>
                  </div>
                  {p?.address ? (
                    <div className="text-xs text-slate-700 mt-0.5">{p.address}</div>
                  ) : null}
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono-data tabular-nums">
                    {mapsUrl ? (
                      <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                        className="hover:underline text-[#116B6E]"
                        data-testid={`cv-detail-gps-${i}-open`}>
                        {formatCoord(p)}
                      </a>
                    ) : formatCoord(p)}
                    {typeof p?.accuracy_m === "number" && (
                      <> · ±{Math.round(p.accuracy_m)}m</>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono-data tabular-nums">
                    {(p?.recorded_at || p?.captured_at || "").replace("T", " ").slice(0, 19)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


const _ROLE_LABEL_MAP = {
  contact_supervisor: "Contact-centre supervisor",
  mediator:           "Mediator",
  therapist:          "Therapist",
  teacher:            "Teacher",
  social_worker:      "Social worker",
  family_member:      "Family member",
  other:              "Other",
};
const ROLE_LABEL = (k) => _ROLE_LABEL_MAP[k] || (k || "Observer");

function InviteStatusPill({ status, expiresAt, openedAt, startedAt }) {
  // Iter 122.invite-lifecycle — richer observer status pill.
  // Lifecycle: Invited → Opened → Started → Submitted.
  // Pending invites resolve to the most progressed observed state
  // (started > opened > pending) so the parent sees signs of life
  // even before submission.
  const expired = status === "pending" && expiresAt && expiresAt < new Date().toISOString();
  let eff = expired ? "expired" : status;
  if (eff === "pending") {
    if (startedAt) eff = "started";
    else if (openedAt) eff = "opened";
    else eff = "invited";
  }
  const TONE = {
    invited:   "info",
    opened:    "attention",
    started:   "attention",
    submitted: "positive",
    pending:   "info",
    expired:   "calm",
    revoked:   "alert",
  };
  const LABEL = {
    invited:   "Invited",
    opened:    "Opened",
    started:   "Started",
    submitted: "Submitted",
    pending:   "Invited",
    expired:   "Expired",
    revoked:   "Revoked",
  };
  return (
    <StatusPill tone={TONE[eff] || "calm"} testId={`cv-invite-pill-${eff}`}>
      {LABEL[eff] || eff.replace(/_/g, " ")}
    </StatusPill>
  );
}

function VisitDetail({ visit, onClose, onChanged, householdChildren, onRequestEdit }) {
  const [tab, setTab] = useState("overview");
  const [actNote, setActNote] = useState("");
  if (!visit) return null;
  const childMap = Object.fromEntries((householdChildren || []).map((c) => [c.id, c]));

  const verify = async (decision) => {
    try {
      await api.post(`/contact-visits/${visit.id}/verify`, { decision, note: actNote || undefined });
      toast.success(decision === "confirmed" ? "Handover confirmed"
        : "Marked as disputed — both records preserved side-by-side");
      setActNote("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record"); }
  };

  const lifecycleAction = async (path, payload, msg) => {
    try {
      await api.post(`/contact-visits/${visit.id}/${path}`, payload || {});
      toast.success(msg);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  // End-visit capture: ask for GPS (consent) before ending so the
  // record has both endpoints. The toast only claims "location saved"
  // once the backend has acknowledged the request — never on the
  // client-side reading alone.
  const endVisitWithGps = async () => {
    const gps = await captureGps();
    try {
      await api.post(`/contact-visits/${visit.id}/end`, {
        outcome: "completed",
        device: navigator.userAgent.slice(0, 80),
        gps: gps || undefined,
      });
      toast.success(gps ? "Visit ended · end location saved" : "Visit ended");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not end visit");
    }
  };

  const v = visit.verification || {};
  const visitChildren = (visit.child_ids || []).map((id) => childMap[id]).filter(Boolean);
  const editable = ["scheduled", "confirmed", "rescheduled"].includes(visit.status);

  return (
    <Sheet open onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="bottom" className="rounded-t-xl p-0 max-h-[92vh] overflow-y-auto sm:max-w-2xl sm:mx-auto" data-testid="cv-detail-panel">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-baseline justify-between gap-2">
            <SheetTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-700" />
              {visit.visit_date} · {typeLabel(visit.visit_type)}
            </SheetTitle>
            <StatusBadge status={visit.status} />
          </div>
          {visit.evidence_ref && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="text-[11px] text-slate-500 font-mono-data select-all" data-testid="cv-detail-evidence-ref">
                Record reference:{" "}
                <span className="text-slate-700 font-medium select-all">{visit.evidence_ref}</span>
              </span>
              <InlineCopyIcon
                text={formatCitation(visit)}
                testid="cv-copy-evidence-ref-icon"
                tooltip="Copy reference"
              />
            </div>
          )}
        </SheetHeader>

        {/* Tabs */}
        <div className="px-5 pt-4 flex gap-1 border-b border-slate-100 -mb-px overflow-x-auto">
          {[
            { v: "overview",  l: "Overview",        icon: Eye },
            { v: "notes",     l: "Notes",           icon: MessageSquare },
            { v: "observers", l: "Observers",       icon: UserRoundCheck },
            { v: "timeline",  l: "Audit trail",     icon: ScrollText },
            { v: "versions",  l: "Version history", icon: History },
          ].map(({ v: tv, l, icon: Icon }) => (
            <button key={tv} onClick={() => setTab(tv)} data-testid={`cv-detail-tab-${tv}`}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 border-b-2 transition-colors ${
                tab === tv ? "border-teal-700 text-teal-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>

        <div className="px-5 py-4 space-y-5">
          {tab === "overview" && (
            <>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2">
                  <dt className="text-[10px] uppercase tracking-wider text-slate-500">Location</dt>
                  <dd className="text-slate-900">
                    <LocationOverview visit={visit} />
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wider text-slate-500">Outcome</dt>
                  <dd className="text-slate-900">{(visit.outcome || "—").replace(/_/g, " ")}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wider text-slate-500">Verification</dt>
                  <dd className="text-slate-900">{(v.aggregate || "pending").replace(/_/g, " ")}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-[10px] uppercase tracking-wider text-slate-500">Children</dt>
                  <dd className="text-slate-900">
                    {visitChildren.length === 0 ? "All children" : visitChildren.map((c) => c.name).join(", ")}
                  </dd>
                </div>
              </dl>

              {(visit.people_present || []).length > 0 && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <UserRoundCheck className="w-3.5 h-3.5" /> Participants
                  </h3>
                  <ul className="space-y-1.5" data-testid="cv-detail-people">
                    {visit.people_present.map((p, i) => {
                      const roleLabel = (ROLE_OPTIONS.find((r) => r[0] === p.role) || ["other","Other"])[1];
                      return (
                        <li key={i} className="flex items-center gap-2 bg-slate-50 rounded-sm px-2.5 py-2 text-sm">
                          <span className="text-slate-900 flex-1 truncate">{p.name}</span>
                          <Badge className="bg-teal-50 text-teal-800 border border-teal-200 rounded-sm text-[10px] uppercase tracking-wider font-medium">{roleLabel}</Badge>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {visit.status === "disputed" && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-sm" data-testid="cv-detail-disputed-banner">
                  <div className="text-xs text-rose-900 font-medium flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> This visit is disputed
                  </div>
                  <p className="text-xs text-slate-700 mt-1 leading-relaxed">
                    Both parents&apos; records are preserved side-by-side. Original submissions cannot be amended.
                    Use the dispute response notes to add supporting commentary &mdash; everything is retained in the audit trail.
                  </p>
                </div>
              )}

              {visit.status === "in_progress" && (
                <ActiveSessionPanel visit={visit} onChanged={onChanged} />
              )}

              {/* Lifecycle actions */}
              <section className="space-y-2">
                <h3 className="text-[11px] uppercase tracking-wider text-slate-500">Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  {visit.status === "scheduled" && (
                    <>
                      <Button variant="outline" onClick={() => lifecycleAction("confirm", null, "Visit confirmed")} className="h-11 rounded-sm" data-testid="cv-action-confirm">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark as confirmed
                      </Button>
                      <Button onClick={() => lifecycleAction("start", { device: navigator.userAgent.slice(0, 80) }, "Visit started")}
                        className="h-11 rounded-sm bg-teal-700 hover:bg-teal-800 text-white" data-testid="cv-action-start">
                        <Play className="w-4 h-4 mr-1.5" /> Start visit
                      </Button>
                    </>
                  )}
                  {visit.status === "confirmed" && (
                    <Button onClick={() => lifecycleAction("start", { device: navigator.userAgent.slice(0, 80) }, "Visit started")}
                      className="h-11 rounded-sm bg-teal-700 hover:bg-teal-800 text-white col-span-2" data-testid="cv-action-start">
                      <Play className="w-4 h-4 mr-1.5" /> Start visit
                    </Button>
                  )}
                  {visit.status === "in_progress" && (
                    <Button onClick={endVisitWithGps}
                      className="h-11 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white col-span-2" data-testid="cv-action-end">
                      <Square className="w-4 h-4 mr-1.5" /> End visit
                    </Button>
                  )}
                  {editable && (
                    <Button variant="outline" onClick={() => onRequestEdit?.(visit)}
                      className="h-11 rounded-sm" data-testid="cv-action-edit">
                      <Pencil className="w-4 h-4 mr-1.5" /> Edit details
                    </Button>
                  )}
                  {(visit.status === "scheduled" || visit.status === "confirmed") && (
                    <Button variant="outline" onClick={() => {
                      const reason = window.prompt("Please record the reason for cancellation:");
                      if (!reason) return;
                      lifecycleAction("cancel", { reason }, "Visit cancelled");
                    }} className="h-11 rounded-sm border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="cv-action-cancel">
                      <X className="w-4 h-4 mr-1.5" /> Cancel visit
                    </Button>
                  )}
                </div>
              </section>

              {visit.status === "awaiting_verification" && (
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Verification note (optional)</Label>
                  <Textarea rows={2} value={actNote} onChange={(e) => setActNote(e.target.value)} data-testid="cv-detail-act-note"
                    placeholder="Optional context to accompany your verification decision" />
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={() => verify("confirmed")} className="h-12 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="cv-verify-confirm">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm record
                    </Button>
                    <Button onClick={() => verify("disputed")} variant="outline" className="h-12 rounded-sm border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="cv-verify-dispute">
                      <AlertTriangle className="w-4 h-4 mr-1.5" /> Mark as disputed
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "notes" && <NotesTab visit={visit} onAdded={onChanged} />}

          {tab === "observers" && <ObserversTab visit={visit} />}

          {tab === "timeline" && (
            <ul className="space-y-1.5" data-testid="cv-detail-timeline">
              {(visit.timeline || []).length === 0
                ? <p className="text-xs text-slate-500">No timeline entries yet.</p>
                : (visit.timeline || []).map((e) => {
                    const isGps = e.action === "gps_captured";
                    const payload = e.payload || {};
                    return (
                      <li key={e.id} className="text-xs text-slate-700 flex gap-3"
                        data-testid={`cv-timeline-row-${e.action}`}>
                        <span className="font-mono-data text-slate-500 w-32 flex-shrink-0">
                          {e.at?.slice(0, 16).replace("T", " ")}
                        </span>
                        <span className="text-slate-900 flex-1 min-w-0">
                          {isGps ? (
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin className="w-3 h-3 text-[#1F9FA4]" />
                              {payload.phase === "end" ? "End location captured" : "Start location captured"}
                              <span className="text-[11px] text-slate-500 font-mono-data tabular-nums">
                                {typeof payload.lat === "number" && typeof payload.lng === "number"
                                  ? `· ${payload.lat.toFixed(4)}, ${payload.lng.toFixed(4)}`
                                  : ""}
                                {typeof payload.accuracy_m === "number"
                                  ? ` · ±${Math.round(payload.accuracy_m)}m`
                                  : ""}
                                {payload.source === "user_entered" ? " · manual" : ""}
                              </span>
                            </span>
                          ) : (
                            (e.action || "").replace(/_/g, " ")
                          )}
                        </span>
                      </li>
                    );
                  })}
            </ul>
          )}

          {tab === "versions" && <VersionsTab visit={visit} />}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3">
          <Button onClick={onClose} variant="outline" className="w-full h-12 rounded-sm" data-testid="cv-detail-close">
            <X className="w-4 h-4 mr-1.5" /> Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ===== Filterable KPI tile ===================================================
// Iter 122.cv-overhaul — KPI cards must behave as filter shortcuts per spec.
// Wraps the design-system KpiTile semantics in an accessible <button> with
// pressed state when active. Disabled tiles (informational only, e.g.
// Average punctuality) stay non-interactive.

function FilterableKpiTile({ label, value, sub, tone = "calm", active, onClick, disabled, testId }) {
  const TONE = {
    calm:      { bg: "#EEF3F4", border: "#D9E1E3", text: "#2E3A3F", dot: "#9DB0B5" },
    info:      { bg: "#E6ECEF", border: "#B6C5CC", text: "#2E3E48", dot: "#4A6170" },
    positive:  { bg: "#D6F0EF", border: "#A7D8DA", text: "#0F5658", dot: "#1F9FA4" },
    attention: { bg: "#FBF0DC", border: "#E2C588", text: "#6F4E1E", dot: "#B88A3A" },
    alert:     { bg: "#FBE6E3", border: "#E8B6AF", text: "#8C1F12", dot: "#C0291D" },
  };
  const c = TONE[tone] || TONE.calm;
  const cls = "rounded-md p-3.5 relative overflow-hidden block w-full text-left transition-all "
    + (disabled
        ? "bg-white border border-[#D9E1E3] cursor-default"
        : active
          ? "bg-white border-2 ring-2 ring-offset-1"
          : "bg-white border border-[#D9E1E3] hover:bg-[#F4FAFA] active:bg-[#EEF3F4]");
  const style = active && !disabled
    ? { borderColor: c.dot, boxShadow: `inset 0 0 0 1px ${c.dot}`, ringColor: c.dot }
    : {};
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={active ? "true" : "false"}
      className={cls}
      style={style}
      data-testid={testId}
    >
      <span
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ backgroundColor: c.dot }}
        aria-hidden="true"
      />
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mt-1">
        {label}
      </div>
      <div
        className="font-heading text-lg font-semibold tabular-nums mt-1.5 leading-tight"
        style={{ color: c.text }}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-[11px] text-[#6F828A] mt-1 leading-snug">{sub}</div>
      ) : null}
    </button>
  );
}

// ===== Observer invite editor (used in schedule + live setup) ==============

const OBSERVER_ROLE_CHOICES = [
  ["contact_supervisor", "Contact-centre supervisor"],
  ["mediator",           "Mediator"],
  ["therapist",          "Therapist / counsellor"],
  ["teacher",            "Teacher"],
  ["social_worker",      "Social worker"],
  ["family_member",      "Family member"],
  ["other",              "Other"],
];

function PendingObserverEditor({ value, onChange, testidPrefix = "cv-obs" }) {
  // Pre-flight invites the user is preparing. They are sent the moment
  // the parent visit row exists (i.e. after the scheduling create call
  // or after the live visit is started). We never round-trip the
  // backend until the visit exists, otherwise we would create
  // orphaned invites if the user cancels the dialog.
  const [draft, setDraft] = useState({
    email: "", name: "", organisation: "",
    role: "contact_supervisor", context_note: "",
  });
  const canAdd = draft.email.trim() && draft.name.trim();
  const add = () => {
    if (!canAdd) return;
    onChange([...(value || []), { ...draft }]);
    setDraft({ email: "", name: "", organisation: "", role: "contact_supervisor", context_note: "" });
  };
  const remove = (i) => onChange((value || []).filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-editor`}>
      {(value || []).length > 0 && (
        <ul className="space-y-1.5">
          {value.map((p, i) => (
            <li key={i}
              className="flex items-center gap-2 bg-slate-50 rounded-sm px-2.5 py-2 text-sm"
              data-testid={`${testidPrefix}-row-${i}`}>
              <span className="text-slate-900 truncate flex-1">
                {p.name} <span className="text-slate-500">&lt;{p.email}&gt;</span>
              </span>
              <Badge className="bg-teal-50 text-teal-800 border border-teal-200 rounded-sm text-[10px] uppercase tracking-wider font-medium">
                {(OBSERVER_ROLE_CHOICES.find((r) => r[0] === p.role) || ["other","Other"])[1]}
              </Badge>
              <button type="button" onClick={() => remove(i)}
                className="text-slate-400 hover:text-rose-700 p-1"
                aria-label="Remove"
                data-testid={`${testidPrefix}-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Input value={draft.name} placeholder="Full name"
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          className="h-10 text-sm"
          data-testid={`${testidPrefix}-name`} />
        <Input value={draft.email} placeholder="Email" type="email"
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          className="h-10 text-sm"
          data-testid={`${testidPrefix}-email`} />
        <select value={draft.role}
          onChange={(e) => setDraft({ ...draft, role: e.target.value })}
          className="h-10 text-sm border border-slate-200 rounded-sm px-2 bg-white"
          data-testid={`${testidPrefix}-role`}>
          {OBSERVER_ROLE_CHOICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input value={draft.organisation} placeholder="Organisation (optional)"
          onChange={(e) => setDraft({ ...draft, organisation: e.target.value })}
          className="h-10 text-sm"
          data-testid={`${testidPrefix}-org`} />
      </div>
      <div className="flex justify-end">
        <Button type="button" onClick={add} disabled={!canAdd}
          variant="outline" className="h-9 rounded-sm text-xs px-3"
          data-testid={`${testidPrefix}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Queue observer
        </Button>
      </div>
      <p className="text-[11px] text-slate-500 leading-snug flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        Each observer receives a secure single-use link by email. After the visit is saved,
        you can also copy each link to share by another channel.
      </p>
    </div>
  );
}

// Helper: send a list of pending observer invites for a freshly-created
// visit and return the URLs back to the caller so the UI can offer
// "Copy link" affordances. Failures are toasted but do not block the
// happy path — the visit row already exists.
async function sendPendingObserverInvites(visitId, pending) {
  if (!pending || pending.length === 0) return [];
  const out = [];
  for (const p of pending) {
    try {
      const r = await api.post(`/contact-visits/${visitId}/facilitator-invites`, {
        email: p.email, name: p.name,
        organisation: p.organisation || undefined,
        role: p.role || "contact_supervisor",
        context_note: p.context_note || undefined,
      });
      out.push({ email: p.email, name: p.name, link: r.data?.one_time_link, ok: true });
    } catch (e) {
      out.push({ email: p.email, name: p.name, ok: false,
                  error: e?.response?.data?.detail || e.message });
    }
  }
  return out;
}

// ===== Start Live Visit setup sheet ========================================

/** Try to capture a GPS reading with consent. Resolves to `null` if
 *  the user denies or the API is unavailable; never throws. */
function captureGps() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          captured_at: new Date(pos.timestamp).toISOString(),
          source: "auto_gps",
        });
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  });
}

function StartLiveVisitSetupSheet({ open, onClose, onStarted, householdChildren, visits }) {
  // Iter 122.start-live — Setup screen shown BEFORE the timer starts.
  // Captures visit type, GPS (with consent), venue, observers and
  // pre-visit notes. If a scheduled visit already exists for today
  // (or within the next 4 hours), it is offered as a "Convert to
  // live" candidate so the parent does not create a duplicate record.

  // Candidate scheduled visits (today or imminent)
  const candidates = useMemo(() => {
    const now = Date.now();
    const horizon = now + 4 * 60 * 60 * 1000; // 4h ahead
    const todayIso = new Date().toISOString().slice(0, 10);
    return (visits || []).filter((v) => {
      if (!["scheduled", "confirmed", "rescheduled"].includes(v.status)) return false;
      if (v.visit_date === todayIso) return true;
      try {
        const t = new Date(v.scheduled_start).getTime();
        return Number.isFinite(t) && t <= horizon && t >= now - 60 * 60 * 1000;
      } catch { return false; }
    });
  }, [visits]);

  const [convertId, setConvertId] = useState(null);
  const [form, setForm] = useState({
    visit_type: "parent_to_parent_handover",
    location: "",
    notes: "",
    child_ids: [],
    pending_invites: [],
  });
  const [gps, setGps] = useState(null);
  const [gpsState, setGpsState] = useState("idle"); // idle | capturing | granted | denied
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Default to converting the most imminent candidate, if any.
    if (candidates.length > 0) {
      const top = candidates[0];
      setConvertId(top.id);
      setForm({
        visit_type: top.visit_type || "parent_to_parent_handover",
        location: top.location || "",
        notes: "",
        child_ids: top.child_ids || [],
        pending_invites: [],
      });
    } else {
      setConvertId(null);
      setForm({
        visit_type: "parent_to_parent_handover",
        location: "",
        notes: "",
        child_ids: [],
        pending_invites: [],
      });
    }
    setGps(null);
    setGpsState("idle");
  }, [open, candidates]);

  const requestGps = async () => {
    setGpsState("capturing");
    const pt = await captureGps();
    if (pt) { setGps(pt); setGpsState("granted"); }
    else    { setGps(null); setGpsState("denied"); }
  };

  const submit = async () => {
    setStarting(true);
    try {
      let visitId = convertId;
      // 1. If converting an existing scheduled visit, patch its
      //    factual fields (only those still patchable pre-start).
      if (convertId) {
        const patch = {
          visit_type: form.visit_type,
          location: form.location || undefined,
          child_ids: form.child_ids,
        };
        try { await api.patch(`/contact-visits/${convertId}`, patch); }
        catch (e) { console.warn("patch convert", e?.response?.data || e); }
      } else {
        // 2. Otherwise create a new scheduled-at-now record.
        const nowDate = new Date();
        const isoDate = nowDate.toISOString().slice(0, 10);
        const hhmm = `${String(nowDate.getHours()).padStart(2, "0")}:${String(nowDate.getMinutes()).padStart(2, "0")}`;
        const startIso = `${isoDate}T${hhmm}:00+00:00`;
        const created = await api.post("/contact-visits", {
          visit_type: form.visit_type,
          visit_date: isoDate,
          scheduled_start: startIso,
          scheduled_end: startIso,
          location: form.location || undefined,
          child_ids: form.child_ids,
          people_present: [],
          notes: form.notes || undefined,
        });
        visitId = created.data?.id;
      }
      if (!visitId) throw new Error("Could not resolve visit");

      // 3. Send observer invites (best-effort).
      const inviteResults = await sendPendingObserverInvites(visitId, form.pending_invites);
      const okCount = inviteResults.filter((r) => r.ok).length;
      if (okCount > 0) {
        const lastLink = inviteResults.find((r) => r.ok && r.link)?.link;
        try { if (lastLink) await navigator.clipboard?.writeText(lastLink); } catch { /* clipboard blocked */ }
        toast.success(
          okCount === 1 && lastLink
            ? "Observer invited · link copied to clipboard"
            : `${okCount} observer${okCount === 1 ? "" : "s"} invited`,
        );
      }

      // 4. Transition the visit to in_progress with GPS + setup payload.
      try {
        await api.post(`/contact-visits/${visitId}/start`, {
          device: navigator.userAgent.slice(0, 80),
          gps: gps || undefined,
          visit_type: form.visit_type,
          location: form.location || undefined,
          notes: form.notes || undefined,
        });
        const locMsg = gps ? " · start location saved" : "";
        toast.success(
          (convertId ? "Scheduled visit converted to live" : "Live visit started") + locMsg,
        );
        onStarted?.(visitId);
      } catch (err) {
        const det = err?.response?.data?.detail;
        if (det && typeof det === "object" && det.error === "active_visit_exists" && det.active_visit_id) {
          toast.info("Another visit is already in progress. Opening it now.");
          onStarted?.(det.active_visit_id);
        } else {
          toast.error(typeof det === "string" ? det : "Could not start visit");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start visit");
    } finally {
      setStarting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="bottom" className="rounded-t-xl p-0 max-h-[92vh] overflow-y-auto sm:max-w-2xl sm:mx-auto" data-testid="cv-live-setup-sheet">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <SheetTitle className="text-base font-semibold flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#1F9FA4]" />
            Set up a live visit
          </SheetTitle>
          <p className="text-xs text-slate-500 text-left leading-relaxed">
            A few details before the timer starts — these stamp the record at the
            moment the visit begins so your record is complete.
          </p>
        </SheetHeader>

        <div className="px-5 py-4 space-y-5">
          {/* Scheduled visit detection */}
          {candidates.length > 0 && (
            <section data-testid="cv-live-setup-candidates">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                A scheduled visit is ready to begin
              </Label>
              <p className="text-[11px] text-slate-500 mt-1 mb-2 leading-relaxed">
                Use the existing scheduled record so everything stays in a single record.
              </p>
              <div className="space-y-1.5">
                {candidates.map((c) => (
                  <label key={c.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer transition-colors ${
                      convertId === c.id
                        ? "border-teal-700 bg-teal-50"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                    data-testid={`cv-live-setup-candidate-${c.id}`}>
                    <input
                      type="radio"
                      name="cv-convert"
                      checked={convertId === c.id}
                      onChange={() => setConvertId(c.id)}
                      className="accent-teal-700"
                    />
                    <span className="text-sm text-slate-900 flex-1 truncate">
                      Convert &nbsp;<span className="font-mono-data text-[11px] text-slate-500">{c.evidence_ref}</span> · {typeLabel(c.visit_type)}
                    </span>
                    <span className="text-[11px] text-slate-500 font-mono-data tabular-nums">
                      {(c.scheduled_start || "").slice(11, 16)}
                    </span>
                  </label>
                ))}
                <label className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer transition-colors ${
                  convertId === null
                    ? "border-teal-700 bg-teal-50"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`} data-testid="cv-live-setup-create-new">
                  <input type="radio" name="cv-convert"
                    checked={convertId === null}
                    onChange={() => setConvertId(null)}
                    className="accent-teal-700" />
                  <span className="text-sm text-slate-900 flex-1">Don&apos;t convert — start a fresh record</span>
                </label>
              </div>
            </section>
          )}

          {/* Visit type */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Visit type</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {QUICK_TYPES.map(({ v, l, icon: Icon }) => (
                <button key={v} type="button"
                  onClick={() => setForm((f) => ({ ...f, visit_type: v }))}
                  data-testid={`cv-live-setup-type-${v}`}
                  className={`min-h-[60px] flex flex-col items-center justify-center gap-1 rounded-sm border text-xs px-2 py-2 transition-colors ${
                    form.visit_type === v ? "border-teal-700 bg-teal-50 text-teal-900"
                                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}>
                  <Icon className="w-4 h-4" strokeWidth={1.6} />
                  <span className="leading-tight text-center">{l}</span>
                </button>
              ))}
            </div>
            <select
              value={form.visit_type}
              onChange={(e) => setForm({ ...form, visit_type: e.target.value })}
              className="mt-2 w-full text-sm border border-slate-200 rounded-sm h-10 px-2 bg-white"
              data-testid="cv-live-setup-type-more">
              <option disabled>More visit types…</option>
              {MORE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </section>

          {/* GPS */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Location record (optional)</Label>
            <p className="text-[11px] text-slate-500 mt-1 mb-2 leading-relaxed flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              Capturing GPS strengthens the record. We never share location with the other parent —
              it is sealed alongside the visit&apos;s audit trail.
            </p>
            {gpsState === "granted" && gps ? (
              <div className="rounded-sm border border-[#A7D8DA] bg-[#D6F0EF]/40 p-3 text-xs text-[#0F5658]" data-testid="cv-live-setup-gps-captured">
                <div className="flex items-center gap-1.5 font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Location reading ready
                </div>
                <div className="mt-1 font-mono-data tabular-nums">
                  {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} · ±{Math.round(gps.accuracy_m || 0)}m
                </div>
                <p className="text-[11px] text-[#116B6E] mt-1 leading-snug">
                  This reading is saved to the visit record the moment you tap “Start visit”.
                </p>
                <button type="button" onClick={() => { setGps(null); setGpsState("idle"); }}
                  className="text-[11px] text-[#116B6E] hover:underline mt-1"
                  data-testid="cv-live-setup-gps-clear">
                  Don&apos;t include location
                </button>
              </div>
            ) : (
              <Button type="button" onClick={requestGps} variant="outline"
                disabled={gpsState === "capturing"}
                className="h-10 rounded-sm text-xs"
                data-testid="cv-live-setup-gps-capture">
                <MapPin className="w-3.5 h-3.5 mr-1.5" />
                {gpsState === "capturing" ? "Reading location…"
                  : gpsState === "denied"  ? "Location unavailable — tap to retry"
                                           : "Capture location with consent"}
              </Button>
            )}
          </section>

          {/* Venue */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Venue (optional)</Label>
            <Input value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. Contact centre, school, park"
              className="h-11 text-base mt-2" data-testid="cv-live-setup-location" />
          </section>

          {/* Children */}
          {householdChildren?.length > 0 && (
            <section>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Children
              </Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setForm({ ...form, child_ids: [] })}
                  data-testid="cv-live-setup-child-all"
                  className={`min-h-[36px] px-3 rounded-full text-xs font-medium transition-colors ${
                    (form.child_ids?.length || 0) === 0 ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}>
                  All children
                </button>
                {householdChildren.map((c) => {
                  const selected = form.child_ids.includes(c.id);
                  return (
                    <button key={c.id} type="button"
                      onClick={() => setForm((f) => ({
                        ...f,
                        child_ids: selected ? f.child_ids.filter((x) => x !== c.id) : [...f.child_ids, c.id],
                      }))}
                      data-testid={`cv-live-setup-child-${c.id}`}
                      className={`min-h-[36px] px-3 rounded-full text-xs font-medium transition-colors ${
                        selected ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Observers */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <UserRoundCheck className="w-3 h-3" /> Observers (optional)
            </Label>
            <p className="text-[11px] text-slate-500 mt-1 mb-2 leading-relaxed">
              Each observer receives a secure single-use link by email. You can also copy
              the link to share by another channel once the visit starts.
            </p>
            <PendingObserverEditor
              value={form.pending_invites}
              onChange={(arr) => setForm({ ...form, pending_invites: arr })}
              testidPrefix="cv-live-setup-obs"
            />
          </section>

          {/* Pre-visit notes */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Pre-visit notes (private)
            </Label>
            <Textarea rows={3} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything you want recorded as the visit starts…"
              className="text-base mt-2" data-testid="cv-live-setup-notes" />
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}
            className="flex-1 h-12 rounded-sm" data-testid="cv-live-setup-cancel">
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={starting}
            className="flex-[2] h-12 rounded-sm bg-[#1F9FA4] hover:bg-[#178085] text-white text-base font-medium"
            data-testid="cv-live-setup-start">
            {starting ? "Starting…" : (convertId ? "Convert & start" : "Start visit")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ===== Main page ============================================================

export default function ContactVisits() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [visits, setVisits] = useState([]);
  const [dash, setDash] = useState(null);
  const [children, setChildren] = useState([]);
  const [error, setError] = useState(null);
  const [formMode, setFormMode] = useState(null); // "schedule" | "record" | "live" | "edit"
  const [editTarget, setEditTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [entryOpen, setEntryOpen] = useState(false); // FAB popover
  const [liveSetupOpen, setLiveSetupOpen] = useState(false);

  // Iter 122.cv-overhaul — filter state is URL-driven so refresh /
  // sharing preserves the user's selection. Tap a KPI = set filter,
  // tap again or "Clear filter" = unset.
  const filter = searchParams.get("filter") || null;
  const setFilter = (f) => {
    const next = new URLSearchParams(searchParams);
    if (!f) next.delete("filter"); else next.set("filter", f);
    setSearchParams(next, { replace: true });
  };
  const toggleFilter = (f) => setFilter(filter === f ? null : f);

  const reload = async () => {
    try {
      const [v, d, ch] = await Promise.all([
        api.get("/contact-visits"),
        api.get("/contact-visits/dashboard"),
        api.get("/children"),
      ]);
      setVisits(v.data || []);
      setDash(d.data);
      setChildren(ch.data || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { reload(); }, []);

  const openVisit = async (id) => {
    try {
      const r = await api.get(`/contact-visits/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Could not load visit");
    }
  };

  // ?visit={id} deep-link (used by the Calendar integration's "View Visit").
  useEffect(() => {
    const target = searchParams.get("visit");
    if (target && (!detail || detail.id !== target)) {
      openVisit(target);
    }
     
  }, [searchParams]);

  // Iter 122.cv-overhaul — pre-filter the visits list based on the
  // currently-active KPI filter (URL-backed) before grouping. This is
  // what makes the KPI cards behave as navigation shortcuts, per spec.
  const filteredVisits = useMemo(() => {
    if (!filter) return visits;
    const monthStart = new Date();
    monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
    return visits.filter((v) => {
      switch (filter) {
        case "active":
          return v.status === "in_progress";
        case "upcoming":
          return ["scheduled", "confirmed", "rescheduled"].includes(v.status);
        case "awaiting_confirmation":
          return v.status === "scheduled";
        case "awaiting_verification":
          return v.status === "awaiting_verification";
        case "disputed":
          return v.status === "disputed";
        case "awaiting_supervisor":
          return ["in_progress", "awaiting_verification"].includes(v.status)
            && ((v.facilitator_label && v.facilitator_label.length)
                || (v.people_present && v.people_present.length));
        case "month_completed": {
          try {
            const d = v.visit_date ? new Date(v.visit_date) : null;
            return d && d >= monthStart
              && ["completed", "completed_early", "completed_late"].includes(v.outcome);
          } catch { return false; }
        }
        case "missed":
          return v.status === "missed"
            || ["parent_unable", "child_unable", "refused"].includes(v.outcome);
        case "third_party_verified":
          return v.status === "verified_by_professional";
        default:
          return true;
      }
    });
  }, [visits, filter]);

  // Group filtered visits — scheduled vs completed for clearer hierarchy.
  const grouped = useMemo(() => {
    // Iter 122.section-pattern — Reordered by attention priority.
    // Items the user needs to act on appear first; historical sections
    // (Completed, Cancelled) collapse below. Each visit appears in
    // exactly one bucket; the `status` field is the source of truth.
    const inProgress = [];
    const awaitingConfirmation = [];
    const awaitingVerification = [];
    const disputed = [];
    const scheduled = [];
    const completed = [];
    const cancelled = [];
    for (const v of filteredVisits) {
      const s = v.status;
      if (s === "in_progress") inProgress.push(v);
      else if (s === "awaiting_verification") awaitingVerification.push(v);
      else if (s === "disputed") disputed.push(v);
      else if (s === "scheduled") awaitingConfirmation.push(v);
      else if (s === "confirmed" || s === "rescheduled") scheduled.push(v);
      else if (s === "cancelled" || s === "missed") cancelled.push(v);
      else completed.push(v); // completed, verified, closed, completed_*, etc.
    }
    // Sort each list chronologically by scheduled_start (ISO) so the
    // most imminent / most recent rises to the top of each section.
    const startKey = (v) => v.scheduled_start || `${v.visit_date || ""}T00:00:00`;
    const asc  = (a, b) => startKey(a).localeCompare(startKey(b));
    const desc = (a, b) => startKey(b).localeCompare(startKey(a));
    // Forward-looking sections: soonest first. Historical: newest first.
    awaitingConfirmation.sort(asc);
    scheduled.sort(asc);
    inProgress.sort(desc);
    awaitingVerification.sort(desc);
    disputed.sort(desc);
    completed.sort(desc);
    cancelled.sort(desc);
    return { inProgress, awaitingConfirmation, awaitingVerification, disputed, scheduled, completed, cancelled };
  }, [filteredVisits]);

  const punctualityValue = (() => {
    if (dash?.avg_punctuality_minutes == null) return null;
    const m = Math.round(dash.avg_punctuality_minutes);
    if (m === 0) return "On time";
    return m > 0 ? `+${m} min` : `${m} min`;
  })();

  const activeVisit = dash?.active_visit || null;

  const closeDetail = () => {
    setDetail(null);
    if (searchParams.get("visit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("visit");
      setSearchParams(next, { replace: true });
    }
  };

  // FILTER_LABELS used for the "Filtered by …" indicator above the list.
  const FILTER_LABELS = {
    active: "Active visit",
    upcoming: "Upcoming",
    awaiting_confirmation: "Awaiting confirmation",
    awaiting_verification: "Awaiting verification",
    disputed: "Disputed",
    awaiting_supervisor: "Awaiting supervisor notes",
    month_completed: "Completed this month",
    missed: "Missed",
    third_party_verified: "Third-party verified",
  };

  // Open the Start Live Visit setup sheet (visit type, GPS, observers,
  // notes are collected here BEFORE the timer starts). The sheet
  // detects today/imminent scheduled visits and offers a "convert"
  // path so we never create duplicate records.
  const openLiveSetup = () => {
    setEntryOpen(false);
    setLiveSetupOpen(true);
  };

  // iter 126.cv-from-schedule — Materialise upcoming handovers from
  // the parenting schedule so the parent doesn't have to add one
  // Contact Visit per handover manually. Idempotent server-side; safe
  // to invoke repeatedly.
  const [generating, setGenerating] = useState(false);
  const generateFromSchedule = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/contact-visits/generate-from-schedule", { days_ahead: 30 });
      const { created = 0, skipped = 0, handovers = 0 } = r.data || {};
      if (handovers === 0) {
        toast.info("No handovers scheduled in the next 30 days.");
      } else if (created === 0) {
        toast.success(`All ${handovers} upcoming handovers already have a visit record.`);
      } else {
        toast.success(`Generated ${created} contact visit${created === 1 ? "" : "s"} from your parenting schedule.${skipped ? ` (${skipped} already existed.)` : ""}`);
      }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate visits from schedule.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="pb-24 sm:pb-8 max-w-5xl mx-auto" data-testid="contact-visits-page">
      {/* v77.99.108 (iter 122.cv-overhaul) — Header simplified per user
          UX feedback: redundant eyebrow removed. Page title + sub-line
          already orient the user; the previous "Evidence · Shared
          record" eyebrow only restated the module name. The wider
          eyebrow audit across every page is tracked in
          /app/memory/DESIGN_SYSTEM.md (App-wide eyebrow review). */}
      <header className="px-4 sm:px-6 pt-6 mb-4" data-testid="cv-page-header">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#2E3A3F] inline-flex items-center gap-2.5 flex-wrap">
          <ShieldCheck className="w-6 h-6 text-[#116B6E]" strokeWidth={1.7} />
          Contact visits
        </h1>
        <p className="text-sm text-[#6F828A] mt-1.5 leading-snug inline-flex items-center gap-2 flex-wrap">
          A secure, structured record of every contact visit you and your co-parent share.
          <HelpLink
            testid="cv-page-help"
            title="How Contact Visits work"
          >
            <p>
              Every visit is recorded with a complete audit trail to support
              transparency, accountability and clear record keeping.
            </p>
            <p className="mt-2">
              Shared factual records are visible to both parents; private
              notes stay private to their author. The platform records events
              objectively and never decides disputes.
            </p>
          </HelpLink>
        </p>
      </header>

      {/* iter 126.cv-from-schedule — Auto-materialise upcoming
          handovers from the parenting schedule so parents never miss
          a handover log. Idempotent; safe to tap repeatedly. */}
      <div className="px-4 sm:px-6 mb-4 flex justify-end" data-testid="cv-generate-from-schedule-wrap">
        <Button
          onClick={generateFromSchedule}
          disabled={generating}
          variant="outline"
          size="sm"
          className="rounded-sm border-[#A7D8DA] text-[#0F5658] hover:bg-[#E0F2F2]"
          data-testid="cv-generate-from-schedule-btn"
        >
          <Repeat className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.7} />
          {generating ? "Generating…" : "Generate from schedule"}
        </Button>
      </div>

      {/* v77.99.108 (iter 122.cv-overhaul) — Active Visit pinned card.
          Server-of-record session: survives reload, app close, device
          switch. Always rendered above the KPI grid so the user can
          resume in one tap from anywhere on this page. */}
      {activeVisit && (
        <div className="px-4 sm:px-6 mb-4" data-testid="cv-active-pinned">
          <button
            type="button"
            onClick={() => openVisit(activeVisit.id)}
            className="w-full bg-[#D6F0EF] border border-[#A7D8DA] hover:bg-[#C6EAEA] rounded-md px-4 py-3 flex items-center gap-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F9FA4]/55"
            data-testid="cv-active-resume"
          >
            <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1F9FA4] opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#1F9FA4]" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[11px] uppercase tracking-[0.1em] text-[#0F5658] font-medium">
                A visit is in progress
              </span>
              <span className="block text-sm font-medium text-[#0F5658] mt-0.5 truncate">
                {typeLabel(activeVisit.visit_type)}
                {activeVisit.location ? ` · ${activeVisit.location}` : ""}
                {activeVisit.actual_start
                  ? ` · started ${(activeVisit.actual_start || "").slice(11, 16)}`
                  : ""}
              </span>
            </span>
            <span className="text-xs font-medium text-[#0F5658] flex-shrink-0">
              Resume →
            </span>
          </button>
        </div>
      )}

      {/* v77.99.108 (iter 122.cv-overhaul) — KPI cards are now
          interactive filter shortcuts. Tap a tile → list below is
          filtered to that subset; URL ?filter=… preserves the choice
          across refresh. Tap again or "Clear" to remove the filter.
          Primary 4 always visible; remaining behind "More details". */}
      {dash && (
        <div className="px-4 sm:px-6 mb-5" data-testid="cv-kpis">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="cv-kpis-primary">
            <FilterableKpiTile
              label="Upcoming"
              value={dash.upcoming ?? 0}
              sub={dash.next_visit ? `Next ${dash.next_visit.visit_date}` : "None scheduled"}
              tone={(dash.upcoming ?? 0) > 0 ? "info" : "calm"}
              active={filter === "upcoming"}
              onClick={() => toggleFilter("upcoming")}
              testId="cv-tile-upcoming"
            />
            <FilterableKpiTile
              label="Awaiting confirmation"
              value={dash.awaiting_confirmation ?? 0}
              tone={(dash.awaiting_confirmation ?? 0) > 0 ? "attention" : "calm"}
              active={filter === "awaiting_confirmation"}
              onClick={() => toggleFilter("awaiting_confirmation")}
              testId="cv-tile-awaiting-confirmation"
            />
            <FilterableKpiTile
              label="Awaiting verification"
              value={dash.awaiting_verification ?? 0}
              tone={(dash.awaiting_verification ?? 0) > 0 ? "attention" : "calm"}
              active={filter === "awaiting_verification"}
              onClick={() => toggleFilter("awaiting_verification")}
              testId="cv-tile-awaiting-verification"
            />
            <FilterableKpiTile
              label="Disputed"
              value={dash.disputed_awaiting_review ?? 0}
              tone={(dash.disputed_awaiting_review ?? 0) > 0 ? "alert" : "calm"}
              active={filter === "disputed"}
              onClick={() => toggleFilter("disputed")}
              testId="cv-tile-disputed"
            />
          </div>
          <details className="mt-2.5" data-testid="cv-kpis-more">
            <summary className="cursor-pointer text-xs text-[#116B6E] hover:text-[#0F5658] font-medium inline-flex items-center gap-1 list-none [&::-webkit-details-marker]:hidden">
              <ChevronDown className="w-3.5 h-3.5 transition-transform [details[open]_&]:rotate-180" strokeWidth={2} />
              More details
            </summary>
            <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="cv-kpis-secondary">
              <FilterableKpiTile
                label="Awaiting supervisor notes"
                value={dash.awaiting_supervisor_notes ?? 0}
                tone="calm"
                active={filter === "awaiting_supervisor"}
                onClick={() => toggleFilter("awaiting_supervisor")}
                testId="cv-tile-awaiting-supervisor"
              />
              <FilterableKpiTile
                label="Completed this month"
                value={dash.month_completed ?? 0}
                tone="positive"
                active={filter === "month_completed"}
                onClick={() => toggleFilter("month_completed")}
                testId="cv-tile-month"
              />
              <FilterableKpiTile
                label="Missed"
                value={dash.missed ?? 0}
                tone={(dash.missed ?? 0) > 0 ? "alert" : "calm"}
                active={filter === "missed"}
                onClick={() => toggleFilter("missed")}
                testId="cv-tile-missed"
              />
              <FilterableKpiTile
                label="Average punctuality"
                value={punctualityValue ?? "—"}
                tone="calm"
                disabled
                testId="cv-tile-punctuality"
              />
              <FilterableKpiTile
                label="Third-party verified"
                value={dash.third_party_verified ?? 0}
                tone="positive"
                active={filter === "third_party_verified"}
                onClick={() => toggleFilter("third_party_verified")}
                testId="cv-tile-third-party-verified"
              />
            </div>
          </details>
        </div>
      )}

      {error && (
        <div className="px-4 sm:px-6 mb-3">
          <InlineError testId="cv-error">{error}</InlineError>
        </div>
      )}

      {/* Active filter chip — shown when a KPI filter is applied. */}
      {filter && (
        <div className="px-4 sm:px-6 mb-3 flex items-center gap-2" data-testid="cv-filter-chip">
          <span className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] font-medium">
            Showing
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-sm bg-[#D6F0EF] text-[#0F5658] border border-[#A7D8DA]">
            {FILTER_LABELS[filter] || filter}
            <button
              type="button"
              onClick={() => setFilter(null)}
              className="hover:bg-[#A7D8DA]/50 rounded-sm p-0.5 -mr-1"
              aria-label="Clear filter"
              data-testid="cv-filter-clear"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
          <span className="text-[11px] text-[#6F828A] font-mono-data tabular-nums">
            {filteredVisits.length} visit{filteredVisits.length === 1 ? "" : "s"}
          </span>
        </div>
      )}

      {/* Lists — iter 122.section-pattern. Attention-first ordering:
          actionable sections precede historical ones. Every section
          renders at most 5 rows with a "Show all (N more)" toggle that
          persists per-session in sessionStorage. */}
      <div className="px-4 sm:px-6 space-y-5">
        <Section title="In progress"
          rows={grouped.inProgress}
          emptyText={filter ? "No in-progress visits match this filter." : "No visits in progress right now."}
          onOpen={openVisit} idSuffix="in-progress" activeVisitId={activeVisit?.id}
          collapsible defaultLimit={5} storageKey="cv-in-progress-expanded" highlightFirst />

        <Section title="Awaiting confirmation"
          rows={grouped.awaitingConfirmation}
          emptyText={filter ? "No awaiting-confirmation visits match this filter." : "No visits waiting for confirmation."}
          onOpen={openVisit} idSuffix="awaiting-confirmation" activeVisitId={activeVisit?.id}
          collapsible defaultLimit={5} storageKey="cv-awaiting-confirmation-expanded" highlightFirst />

        <Section title="Awaiting verification"
          rows={grouped.awaitingVerification}
          emptyText={filter ? "No awaiting-verification visits match this filter." : "No visits waiting for verification."}
          onOpen={openVisit} idSuffix="awaiting-verification" activeVisitId={activeVisit?.id}
          collapsible defaultLimit={5} storageKey="cv-awaiting-verification-expanded" highlightFirst />

        <Section title="Disputed"
          rows={grouped.disputed}
          emptyText={filter ? "No disputed visits match this filter." : "No disputed visits — calm and clear."}
          onOpen={openVisit} idSuffix="disputed" activeVisitId={activeVisit?.id}
          collapsible defaultLimit={5} storageKey="cv-disputed-expanded" highlightFirst />

        <Section title="Scheduled"
          rows={grouped.scheduled}
          emptyText={filter ? "No scheduled visits match this filter." : "No confirmed scheduled visits."}
          onOpen={openVisit} idSuffix="upcoming" activeVisitId={activeVisit?.id}
          collapsible defaultLimit={5} storageKey="cv-scheduled-expanded" highlightFirst />

        <Section title="Completed"
          rows={grouped.completed}
          emptyText={filter ? "No completed visits match this filter." : "No completed visits on record yet."}
          onOpen={openVisit} idSuffix="completed" activeVisitId={activeVisit?.id}
          collapsible defaultLimit={5} storageKey="cv-completed-expanded" />

        <Section title="Cancelled"
          rows={grouped.cancelled}
          emptyText={filter ? "No cancelled visits match this filter." : "No cancelled visits."}
          onOpen={openVisit} idSuffix="cancelled" activeVisitId={activeVisit?.id}
          collapsible defaultLimit={5} storageKey="cv-cancelled-expanded" />
      </div>

      {/* v77.99.108 (iter 122.cv-overhaul) — Split FAB. Per user spec
          two distinct workflows: "Start live visit" (becomes active
          session, server-of-record) and "Record completed visit"
          (post-hoc entry). "Schedule" stays available for planning. */}
      <div className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-30 flex flex-col items-end gap-2" data-testid="cv-fab-stack">
        {entryOpen && (
          <div className="bg-white border border-[#D9E1E3] rounded-md shadow-lg p-1.5 flex flex-col gap-1 w-60" data-testid="cv-entry-popover">
            <button type="button" onClick={openLiveSetup}
              className="w-full text-left px-3 py-2.5 rounded-sm hover:bg-[#D6F0EF] flex items-start gap-2.5 transition-colors"
              data-testid="cv-entry-live">
              <Zap className="w-4 h-4 text-[#1F9FA4] mt-0.5 flex-shrink-0" strokeWidth={2} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-[#2E3A3F]">Start live visit</span>
                <span className="block text-[11px] text-[#6F828A] leading-snug">Begin a visit happening now. Survives app close.</span>
              </span>
            </button>
            <button type="button" onClick={() => { setEntryOpen(false); setFormMode("record"); }}
              className="w-full text-left px-3 py-2.5 rounded-sm hover:bg-[#EEF3F4] flex items-start gap-2.5 transition-colors"
              data-testid="cv-entry-record">
              <FileText className="w-4 h-4 text-[#116B6E] mt-0.5 flex-shrink-0" strokeWidth={1.7} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-[#2E3A3F]">Record completed visit</span>
                <span className="block text-[11px] text-[#6F828A] leading-snug">Log a visit that already happened.</span>
              </span>
            </button>
            <button type="button" onClick={() => { setEntryOpen(false); setFormMode("schedule"); }}
              className="w-full text-left px-3 py-2.5 rounded-sm hover:bg-[#EEF3F4] flex items-start gap-2.5 transition-colors"
              data-testid="cv-entry-schedule">
              <CalendarDays className="w-4 h-4 text-[#4A6170] mt-0.5 flex-shrink-0" strokeWidth={1.7} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-[#2E3A3F]">Schedule a future visit</span>
                <span className="block text-[11px] text-[#6F828A] leading-snug">Plan a visit ahead of time.</span>
              </span>
            </button>
          </div>
        )}
        <button type="button" onClick={() => setEntryOpen((o) => !o)}
          className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-full shadow-md h-14 px-5 sm:h-12 sm:px-5 flex items-center gap-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1F9FA4]/55 transition-colors"
          aria-expanded={entryOpen ? "true" : "false"}
          data-testid="cv-fab-new">
          {entryOpen ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          {entryOpen ? "Close" : "New contact visit"}
        </button>
      </div>

      <VisitFormSheet
        open={formMode !== null}
        mode={formMode}
        initial={formMode === "edit" ? editTarget : null}
        onClose={() => { setFormMode(null); setEditTarget(null); }}
        onSwitchMode={(m) => setFormMode(m)}
        onSaved={async () => {
          await reload();
          if (detail) await openVisit(detail.id);
        }}
        householdChildren={children}
      />

      {detail && (
        <VisitDetail
          visit={detail}
          householdChildren={children}
          onClose={closeDetail}
          onChanged={async () => { await openVisit(detail.id); await reload(); }}
          onRequestEdit={(v) => { setEditTarget(v); setFormMode("edit"); }}
        />
      )}

      <StartLiveVisitSetupSheet
        open={liveSetupOpen}
        onClose={() => setLiveSetupOpen(false)}
        onStarted={async (vid) => {
          setLiveSetupOpen(false);
          await reload();
          if (vid) await openVisit(vid);
        }}
        householdChildren={children}
        visits={visits}
      />
    </div>
  );
}

function Section({
  title, rows, emptyText, onOpen, idSuffix, activeVisitId,
  // Iter 122.scheduled-collapse — optional collapse behaviour for
  // long sections (currently used for "Scheduled"). When enabled,
  // only `defaultLimit` rows render until the user expands the list.
  // The expanded state is persisted in `sessionStorage` per
  // `storageKey` so it survives navigation within the session but
  // resets next time the user logs in.
  collapsible = false,
  defaultLimit = 5,
  storageKey,
  highlightFirst = false,
}) {
  // v77.99.107 (iter 122.m4) — Section list rebuilt on canonical
  // tokens. White card + #D9E1E3 hairline · #6F828A muted text ·
  // #1F9FA4-tinted hover. Row is tap-anywhere with proper a11y.
  // v77.99.108 (iter 122.cv-overhaul) — Renders an "Active" pill on
  // the matching row so the live session is unmissable.
  // Iter 122.scheduled-collapse — header shows total count, primary
  // (next) row is visually emphasised, list can collapse to top N.
  const [expanded, setExpanded] = useState(() => {
    if (!collapsible || !storageKey) return false;
    try { return sessionStorage.getItem(storageKey) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    if (!collapsible || !storageKey) return;
    try { sessionStorage.setItem(storageKey, expanded ? "1" : "0"); }
    catch { /* private mode etc. — non-fatal */ }
  }, [collapsible, storageKey, expanded]);

  const overLimit = collapsible && rows.length > defaultLimit;
  const visibleRows = overLimit && !expanded ? rows.slice(0, defaultLimit) : rows;
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <div className="bg-white border border-[#D9E1E3] rounded-md overflow-hidden" data-testid={`cv-section-${idSuffix}`}>
      <div className="px-4 py-2.5 border-b border-[#D9E1E3] flex items-baseline justify-between">
        <h2 className="text-xs uppercase tracking-[0.12em] text-[#6F828A] font-medium">
          {title}
          {rows.length > 0 && (
            <span className="ml-1.5 text-[#9DB0B5] font-mono-data normal-case tracking-normal"
              data-testid={`cv-section-${idSuffix}-count`}>
              ({rows.length})
            </span>
          )}
        </h2>
        {overLimit && (
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="text-[11px] text-[#116B6E] hover:text-[#0F5658] font-medium inline-flex items-center gap-1"
            data-testid={`cv-section-${idSuffix}-toggle`}
            aria-expanded={expanded}
          >
            {expanded
              ? "Show fewer"
              : `Show all (${hiddenCount} more)`}
            <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (expanded ? "rotate-180" : "")} />
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-4 text-xs text-[#6F828A]" data-testid={`cv-section-empty-${idSuffix}`}>{emptyText}</p>
      ) : (
        <ul
          className="divide-y divide-[#D9E1E3] transition-[max-height] duration-200 ease-out overflow-hidden"
          data-testid={`cv-section-${idSuffix}-list`}
          style={overLimit
            ? { maxHeight: expanded ? "5000px" : `${visibleRows.length * 72 + 8}px` }
            : undefined}
        >
          {visibleRows.map((v, i) => {
            const isActive = activeVisitId && v.id === activeVisitId;
            const isNext = highlightFirst && i === 0;
            return (
              <li key={v.id}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(v.id)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(v.id); } }}
                className={"px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors "
                  + (isActive
                      ? "bg-[#D6F0EF]/40 hover:bg-[#D6F0EF]/60"
                      : isNext
                        ? "bg-[#F4FAFA] hover:bg-[#D6F0EF]/40 border-l-2 border-[#1F9FA4]"
                        : "hover:bg-[#F4FAFA] active:bg-[#EEF3F4]")}
                data-testid={`cv-row-${v.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    {isNext && (
                      <span className="text-[10px] uppercase tracking-[0.1em] font-medium text-[#1F9FA4]"
                        data-testid={`cv-row-${v.id}-next-pill`}>
                        Next
                      </span>
                    )}
                    <span className="font-mono-data text-[11px] text-[#6F828A] tabular-nums">{v.visit_date}</span>
                    <span className={"text-sm truncate " + (isNext ? "font-semibold text-[#0F5658]" : "font-medium text-[#2E3A3F]")}>
                      {typeLabel(v.visit_type)}
                    </span>
                    {v.evidence_ref && <span className="font-mono-data text-[10px] text-[#9DB0B5] tabular-nums">{v.evidence_ref}</span>}
                  </div>
                  {v.location && <p className="text-xs text-[#6F828A] mt-0.5 truncate">{v.location}</p>}
                </div>
                {isActive ? (
                  <StatusPill tone="positive" testId={`cv-row-${v.id}-active-pill`}>
                    Active
                  </StatusPill>
                ) : (
                  <StatusBadge status={v.status} />
                )}
                <ChevronRight className="w-4 h-4 text-[#9DB0B5] flex-shrink-0" />
              </li>
            );
          })}
        </ul>
      )}
      {/* Footer "show all / fewer" — duplicates the header control so
          the user always has the affordance in reach. */}
      {overLimit && (
        <button
          type="button"
          onClick={() => setExpanded((s) => !s)}
          className="w-full px-4 py-2.5 text-[11px] text-[#116B6E] hover:bg-[#F4FAFA] active:bg-[#EEF3F4] font-medium inline-flex items-center justify-center gap-1 border-t border-[#D9E1E3]"
          data-testid={`cv-section-${idSuffix}-toggle-footer`}
          aria-expanded={expanded}
        >
          {expanded
            ? "Show fewer"
            : `Show all (${hiddenCount} more)`}
          <ChevronDown className={"w-3.5 h-3.5 transition-transform " + (expanded ? "rotate-180" : "")} />
        </button>
      )}
    </div>
  );
}
